import { importMetadataKind, collectImportMetadata, parseImportCollections } from './importCollections';
import { conversationExportRows, describeConversationImportStructure, isChatGPTSummaryRecord } from './conversationImportFormat';

// jszip is ~95 KB and only runs when someone actually imports a conversation archive,
// so it is pulled in on demand rather than pinned into the first-load bundle. This has
// to be a dynamic `import()`: Metro resolves `require('literal')` statically wherever it
// appears, so an inline require would defer execution but still bundle the bytes.
const loadJSZip = async () => (await import('jszip')).default;

/** Accept the optional UTF-8 byte-order mark used by some saved JSON files. */
export function parseConversationJson(jsonString) {
  return JSON.parse(jsonString.replace(/^\uFEFF/, ''));
}

// ─── Format Detection ────────────────────────────────────────────────────────

/**
 * Auto-detects whether parsed JSON is from Gab AI, ChatGPT, Claude, or Grok
 * based on structural differences.
 * Returns 'gabai' | 'chatgpt' | 'claude' | 'grok' | null.
 */
export function detectSource(rawConversations) {
  if (isGabAIExport(rawConversations)) return 'gabai';
  const rows = conversationExportRows(rawConversations);
  if (!rows?.length) return null;
  if (isGrokExport({ conversations: rows })) return 'grok';

  // A partial/empty record at the start must not hide valid conversations later.
  for (const conversation of rows) {
    if (!conversation || typeof conversation !== 'object' || Array.isArray(conversation)) continue;
    if (isGabAIConversation(conversation)) return 'gabai';
    if (conversation.mapping && typeof conversation.mapping === 'object' && !Array.isArray(conversation.mapping)) {
      return 'chatgpt';
    }
    if (Array.isArray(conversation.chat_messages)) return 'claude';
  }
  // Summary records identify the export but cannot supply missing messages.
  // Check them last so incomplete leading records don't hide other providers.
  return rows.some(isChatGPTSummaryRecord) ? 'chatgpt' : null;
}

function isGabAIExport(data) {
  if (data?.object === 'account_data_export') {
    return Array.isArray(data?.data?.conversations);
  }

  return false;
}

function isGabAIConversation(conversation) {
  return (
    Array.isArray(conversation.messages) &&
    !conversation.mapping &&
    !Array.isArray(conversation.chat_messages) &&
    (conversation._id != null || conversation.id != null) &&
    (conversation.createdAt != null || conversation.updatedAt != null || conversation.title != null)
  );
}

function isGrokExport(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const convs = data.conversations;
  if (!Array.isArray(convs) || convs.length === 0) return false;
  const first = convs.find(item => item && typeof item === 'object' &&
    (item.conversation || Array.isArray(item.responses)));
  return (
    first &&
    typeof first === 'object' &&
    (first.conversation || Array.isArray(first.responses))
  );
}

// ─── Shared Helpers ──────────────────────────────────────────────────────────

function normalizeTimestamp(ts) {
  if (typeof ts === 'string') return Number.isFinite(Date.parse(ts)) ? ts : null;
  if (typeof ts !== 'number' || !Number.isFinite(ts)) return null;

  const ms = ts < 4102444800 ? ts * 1000 : ts;
  return Number.isFinite(new Date(ms).getTime()) ? new Date(ms).toISOString() : null;
}

/**
 * Picks the most recent of (a normalized provider-side update timestamp, the
 * last message's timestamp). The chat-history sidebar sorts and labels rows
 * by `updatedAt`, so for imports we want the chat to land at its real
 * recency — not at the day it was first opened, which can be years old.
 *
 * Returns ISO string or null when neither input is usable.
 */
function pickUpdatedAt(providerUpdatedIso, messages) {
  const candidates = [];
  if (providerUpdatedIso) candidates.push(providerUpdatedIso);

  const last = messages?.[messages.length - 1];
  if (last?.timestamp) candidates.push(last.timestamp);

  if (candidates.length === 0) return null;

  let bestIso = null;
  let bestMs = -Infinity;
  for (const iso of candidates) {
    const ms = Date.parse(iso);
    if (!Number.isNaN(ms) && ms > bestMs) {
      bestMs = ms;
      bestIso = iso;
    }
  }
  return bestIso;
}

// ─── ChatGPT Parser ──────────────────────────────────────────────────────────

/**
 * Walks the ChatGPT message tree from root to leaf, following the last child
 * at each branch (ChatGPT stores the "active" path as the last child).
 */
function extractMessageFromChatGPTNode(node) {
  const msg = node?.message;
  if (!msg?.author || !msg.content) return null;

  const role = msg.author.role;
  if (role !== 'user' && role !== 'assistant') return null;

  // Skip messages that aren't part of the rendered conversation:
  //   - `weight: 0` is set on regenerated/inactive branches.
  //   - `metadata.is_visually_hidden_from_conversation` is set on system or
  //     internal scaffolding messages.
  //   - `recipient !== 'all'` indicates a tool/plugin call (browser, python,
  //     dalle.text2im, …) that the user didn't see.
  // Without these filters we'd surface the model's internal tool plumbing
  // as fake "assistant" messages, which then get filtered for empty content
  // and silently shrink the imported message list.
  if (msg.weight === 0) return null;
  if (msg.metadata?.is_visually_hidden_from_conversation === true) return null;
  if (role === 'assistant' && msg.recipient && msg.recipient !== 'all') return null;

  const content = extractChatGPTTextContent(msg.content);
  if (!content || content.trim().length === 0) return null;

  return {
    role,
    content: content.trim(),
    timestamp: msg.create_time ? normalizeTimestamp(msg.create_time) : null,
  };
}

function flattenMessageTreeFromCurrentNode(mapping, currentNodeId) {
  if (!mapping || typeof mapping !== 'object' || !currentNodeId || !mapping[currentNodeId]) {
    return [];
  }

  const chain = [];
  const visited = new Set();
  let currentId = currentNodeId;

  while (currentId) {
    if (visited.has(currentId)) break;
    visited.add(currentId);
    chain.push(currentId);
    currentId = mapping[currentId]?.parent || null;
  }

  chain.reverse();

  const messages = [];
  for (const nodeId of chain) {
    const extracted = extractMessageFromChatGPTNode(mapping[nodeId]);
    if (extracted) messages.push(extracted);
  }

  return messages;
}

function flattenMessageTree(mapping, currentNodeId) {
  if (!mapping || typeof mapping !== 'object') return [];

  // ChatGPT's UI determines the active branch by walking *backward* from
  // `current_node` to the root via `parent` pointers. The forward "last
  // child" walk is a heuristic that diverges whenever the user has
  // regenerated/edited a response (creating sibling branches), so when
  // `current_node` is available we use that path first — it's the one
  // ChatGPT actually rendered for the user.
  if (currentNodeId && mapping[currentNodeId]) {
    const messages = flattenMessageTreeFromCurrentNode(mapping, currentNodeId);
    if (messages.length > 0) return messages;
  }

  // Fallback: walk forward from a root (no parent) by following the last
  // child at each branch. Used when `current_node` is missing or points to
  // a node that's somehow detached. We sort keys so root selection is
  // deterministic across re-imports — Object.keys ordering is not
  // contractual for arbitrary string keys.
  const keys = Object.keys(mapping).sort();

  let rootId = keys.find((id) => {
    const node = mapping[id];
    return node && !node.parent;
  });
  if (!rootId) {
    rootId = keys.find((id) => {
      const node = mapping[id];
      return node && node.parent && !mapping[node.parent];
    });
  }

  if (!rootId) return [];

  const messages = [];
  const visited = new Set();
  let currentId = rootId;

  while (currentId) {
    if (visited.has(currentId)) break;
    visited.add(currentId);

    const node = mapping[currentId];
    if (!node) break;

    const extracted = extractMessageFromChatGPTNode(node);
    if (extracted) messages.push(extracted);

    const children = node.children;
    if (Array.isArray(children) && children.length > 0) {
      currentId = children[children.length - 1];
    } else {
      currentId = null;
    }
  }

  return messages;
}

function extractChatGPTPartText(part) {
  if (typeof part === 'string') return part;
  if (!part || typeof part !== 'object') return '';

  if (typeof part.text === 'string') return part.text;
  if (typeof part.content === 'string') return part.content;

  return '';
}

function extractChatGPTTextContent(content) {
  if (!content || typeof content !== 'object') return '';

  const directFields = ['text', 'result', 'summary', 'user_profile', 'user_instructions'];
  for (const field of directFields) {
    const value = content[field];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  if (Array.isArray(content.parts) && content.parts.length > 0) {
    const joined = content.parts
      .map(extractChatGPTPartText)
      .filter((part) => typeof part === 'string' && part.trim().length > 0)
      .join('\n');
    if (joined.trim()) return joined;
  }

  return '';
}

/**
 * Normalizes a provider-side conversation id to a trimmed string, or null.
 * The backend rejects ids longer than 256 chars; we clip here so valid exports
 * aren't dropped server-side.
 */
function normalizeExternalId(id) {
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }
  if (typeof id !== 'string') return null;
  const trimmed = id.trim();
  if (!trimmed) return null;
  return trimmed.length > 256 ? trimmed.slice(0, 256) : trimmed;
}

export async function parseChatGPTConversations(rawConversations, grouping = new Map()) {
  if (!Array.isArray(rawConversations)) {
    throw new Error('Invalid ChatGPT export: expected an array of conversations');
  }

  const parsed = [];
  const errors = [];
  const missingTrees = [];
  const n = rawConversations.length;

  for (let i = 0; i < n; i++) {
    const conv = rawConversations[i];

    try {
      const title = conv.title || 'Untitled Chat';
      const createdAt = conv.create_time
        ? normalizeTimestamp(conv.create_time)
        : null;

      const messages = flattenMessageTree(conv.mapping, conv.current_node);

      if (messages.length === 0) {
        if (isChatGPTSummaryRecord(conv)) missingTrees.push({ index: i, title,
          externalId: normalizeExternalId(conv.id) || normalizeExternalId(conv.conversation_id) });
        continue;
      }

      // ChatGPT exports carry both `id` (top-level conversation id) and
      // `conversation_id` (sometimes distinct in older exports). Prefer
      // `id` when present; both are stable UUIDs per conversation.
      const externalId = normalizeExternalId(conv.id) || normalizeExternalId(conv.conversation_id);

      const providerUpdatedAt = conv.update_time
        ? normalizeTimestamp(conv.update_time)
        : null;
      const updatedAt = pickUpdatedAt(providerUpdatedAt, messages);

      parsed.push({ title, created_at: createdAt, updated_at: updatedAt, externalId, messages,
        ...(grouping.has(conv) ? { collectionExternalId: grouping.get(conv) } : {}),
      });
    } catch (err) {
      errors.push({ index: i, title: conv?.title || 'Unknown', error: err.message });
    }

    if (i % 50 === 49 && i + 1 < n) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  // Index records can repeat IDs whose full trees appear in numbered files.
  // Only warn about source chats for which no readable copy was supplied.
  const readableIds = new Set(parsed.map(row => row.externalId).filter(Boolean));
  for (const row of missingTrees) {
    if (row.externalId && readableIds.has(row.externalId)) continue;
    errors.push({ index: row.index, title: row.title,
      error: 'The conversation message tree is missing or empty. Select the full export ZIP or all numbered conversations-*.json files to include any available message data.',
    });
  }
  return { conversations: parsed, parseErrors: errors };
}

// ─── Claude Parser ───────────────────────────────────────────────────────────

const CLAUDE_ROLE_MAP = {
  human: 'user',
  assistant: 'assistant',
};
const CLAUDE_ROOT_MESSAGE_IDS = new Set([
  '00000000-0000-4000-8000-000000000000',
  '00000000-0000-0000-0000-000000000000',
]);

function extractClaudeMessageContent(msg) {
  if (typeof msg.text === 'string' && msg.text.trim()) return msg.text;

  if (Array.isArray(msg.content)) {
    return msg.content
      .filter((block) => typeof block === 'string' || (block && block.type === 'text'))
      .map((block) => (typeof block === 'string' ? block : block.text || ''))
      .join('\n');
  }

  return '';
}

function selectClaudeMessageBranch(conversation) {
  const leaf = conversation.current_leaf_message_uuid;
  if (typeof leaf !== 'string' || !leaf) return null;

  const byId = new Map();
  for (const message of conversation.chat_messages) {
    if (typeof message?.uuid !== 'string' || !message.uuid) continue;
    if (byId.has(message.uuid)) return null;
    byId.set(message.uuid, message);
  }
  if (!byId.has(leaf)) return null;

  const chain = [];
  const visited = new Set();
  let currentId = leaf;
  while (currentId) {
    if (visited.has(currentId)) return null;
    visited.add(currentId);
    const message = byId.get(currentId);
    // Use branch filtering only when the full lineage is described. Old flat
    // exports and broken pointers keep every message via the chronological fallback.
    if (!message || !Object.prototype.hasOwnProperty.call(message, 'parent_message_uuid')) return null;
    chain.push(message);
    const parent = message.parent_message_uuid;
    if (parent == null || (!byId.has(parent) && CLAUDE_ROOT_MESSAGE_IDS.has(parent))) {
      return chain.reverse();
    }
    if (typeof parent !== 'string' || !byId.has(parent)) return null;
    currentId = parent;
  }
  return null;
}

export async function parseClaudeConversations(rawConversations, grouping = new Map()) {
  if (!Array.isArray(rawConversations)) {
    throw new Error('Invalid Claude export: expected an array of conversations');
  }

  const parsed = [];
  const errors = [];
  const n = rawConversations.length;

  for (let i = 0; i < n; i++) {
    const conv = rawConversations[i];

    try {
      const title = conv.name || 'Untitled Chat';
      const createdAt = conv.created_at || null;

      const chatMessages = conv.chat_messages;
      if (!Array.isArray(chatMessages) || chatMessages.length === 0) continue;

      // Newer tree-shaped conversation data describes the active branch. Flat
      // exports retain the existing chronological ordering.
      const orderedMessages = selectClaudeMessageBranch(conv) || [...chatMessages].sort((a, b) => {
        const ta = a?.created_at ? Date.parse(a.created_at) : NaN;
        const tb = b?.created_at ? Date.parse(b.created_at) : NaN;
        if (isNaN(ta) && isNaN(tb)) return 0;
        if (isNaN(ta)) return 1;
        if (isNaN(tb)) return -1;
        return ta - tb;
      });

      const messages = [];

      for (const msg of orderedMessages) {
        const role = CLAUDE_ROLE_MAP[msg?.sender];
        if (!role) continue;

        const content = extractClaudeMessageContent(msg);
        if (!content || content.trim().length === 0) continue;

        messages.push({
          role,
          content: content.trim(),
          timestamp: msg.created_at || null,
        });
      }

      if (messages.length === 0) continue;

      // Claude's per-conversation stable id.
      const externalId = normalizeExternalId(conv.uuid) || normalizeExternalId(conv.id);

      const providerUpdatedAt = conv.updated_at || null;
      const updatedAt = pickUpdatedAt(providerUpdatedAt, messages);

      parsed.push({ title, created_at: createdAt, updated_at: updatedAt, externalId, messages,
        ...(grouping.has(conv) ? { collectionExternalId: grouping.get(conv) } : {}),
      });
    } catch (err) {
      errors.push({ index: i, title: conv?.name || 'Unknown', error: err.message });
    }

    if (i % 50 === 49 && i + 1 < n) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  return { conversations: parsed, parseErrors: errors };
}

// ─── Grok Parser ────────────────────────────────────────────────────────────

const GROK_SENDER_MAP = {
  human: 'user',
  assistant: 'assistant',
};

function parseGrokTimestamp(ts) {
  if (ts == null) return null;
  if (typeof ts === 'string' || typeof ts === 'number') return normalizeTimestamp(ts);
  const inner = ts.$date;
  if (typeof inner === 'string') return normalizeTimestamp(inner);
  const value = typeof inner === 'number' ? inner : inner?.$numberLong;
  if (typeof value !== 'number' && (typeof value !== 'string' || !/^-?\d+$/.test(value))) return null;
  // BSON date numbers are milliseconds, including zero and dates before 1970.
  const ms = Number(value);
  return Number.isInteger(ms) && Number.isFinite(new Date(ms).getTime())
    ? new Date(ms).toISOString()
    : null;
}

export async function parseGrokConversations(rawData, grouping = new Map()) {
  const convs = rawData?.conversations;
  if (!Array.isArray(convs)) {
    throw new Error('Invalid Grok export: expected an object with a conversations array');
  }

  const parsed = [];
  const errors = [];
  const n = convs.length;

  for (let i = 0; i < n; i++) {
    const conv = convs[i];

    try {
      const title =
        conv.conversation?.title ||
        conv.title ||
        'Untitled Chat';

      const responses = conv.responses;
      if (!Array.isArray(responses) || responses.length === 0) continue;

      const messages = [];

      for (const entry of responses) {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
        const r = Object.prototype.hasOwnProperty.call(entry, 'response') ? entry.response : entry;
        if (!r || typeof r !== 'object' || Array.isArray(r) || typeof r.sender !== 'string') continue;

        const senderRaw = r.sender.trim().toLowerCase();
        const role = GROK_SENDER_MAP[senderRaw] || (/^grok(?:-|$)/.test(senderRaw) ? 'assistant' : null);
        if (!role) continue;

        const content = typeof r.message === 'string' ? r.message.trim() : '';
        if (!content) continue;

        const timestamp = parseGrokTimestamp(r.create_time);
        messages.push({ role, content, timestamp });
      }

      if (messages.length === 0) continue;

      const createdAt =
        parseGrokTimestamp(conv.conversation?.create_time) ||
        parseGrokTimestamp(conv.create_time) ||
        messages[0].timestamp || null;

      // Grok stable id lives in one of a few places depending on export
      // vintage. Fall back to any present id so dedup still works.
      const externalId =
        normalizeExternalId(conv.conversation?.conversation_id) ||
        normalizeExternalId(conv.conversation_id) ||
        normalizeExternalId(conv.conversation?.id) ||
        normalizeExternalId(conv.conversation?._id) ||
        normalizeExternalId(conv.id) ||
        normalizeExternalId(conv._id);

      // Grok's Mongo dump exposes `modify_time` on the conversation wrapper
      // when present; otherwise fall back to the last message's create_time.
      const providerUpdatedAt =
        parseGrokTimestamp(conv.conversation?.modify_time) ||
        parseGrokTimestamp(conv.modify_time) ||
        null;
      const updatedAt = pickUpdatedAt(providerUpdatedAt, messages);

      parsed.push({ title, created_at: createdAt, updated_at: updatedAt, externalId, messages,
        ...(grouping.has(conv) ? { collectionExternalId: grouping.get(conv) } : {}),
      });
    } catch (err) {
      errors.push({
        index: i,
        title: conv?.conversation?.title || conv?.title || 'Unknown',
        error: err.message,
      });
    }

    if (i % 50 === 49 && i + 1 < n) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  return { conversations: parsed, parseErrors: errors };
}

// ─── Gab AI Parser ───────────────────────────────────────────────────────────

export async function parseGabAIConversations(rawData, grouping = new Map()) {
  const convs =
    rawData?.object === 'account_data_export'
      ? rawData.data?.conversations
      : rawData;

  if (!Array.isArray(convs)) {
    throw new Error(
      'Invalid Gab AI export: expected an account export or conversations array'
    );
  }

  const parsed = [];
  const errors = [];
  const n = convs.length;

  for (let i = 0; i < n; i++) {
    const conv = convs[i];

    try {
      const title = conv.title || 'Untitled Chat';
      const createdAt = conv.createdAt
        ? normalizeTimestamp(conv.createdAt)
        : conv.updatedAt
          ? normalizeTimestamp(conv.updatedAt)
          : null;

      const rawMessages = conv.messages;
      if (!Array.isArray(rawMessages) || rawMessages.length === 0) continue;

      const messages = [];

      for (const msg of rawMessages) {
        if (!msg || typeof msg !== 'object') continue;
        if (msg.flags?.isDeleted || msg.flags?.isHidden) continue;

        const role = msg.role;
        if (role !== 'user' && role !== 'assistant') continue;

        const content = typeof msg.content === 'string' ? msg.content.trim() : '';
        if (!content) continue;

        const timestamp = msg.date
          ? normalizeTimestamp(msg.date)
          : msg.timestamp
            ? normalizeTimestamp(msg.timestamp)
            : null;

        messages.push({ role, content, timestamp });
      }

      if (messages.length === 0) continue;

      const externalId =
        normalizeExternalId(conv._id) ||
        normalizeExternalId(conv.id) ||
        normalizeExternalId(conv.importExternalId) ||
        normalizeExternalId(conv.flags?.importExternalId);

      const providerUpdatedAt = conv.updatedAt
        ? normalizeTimestamp(conv.updatedAt)
        : null;
      const updatedAt = pickUpdatedAt(providerUpdatedAt, messages);

      parsed.push({ title, created_at: createdAt, updated_at: updatedAt, externalId, messages,
        ...(grouping.has(conv) ? { collectionExternalId: grouping.get(conv) } : {}),
      });
    } catch (err) {
      errors.push({ index: i, title: conv?.title || 'Unknown', error: err.message });
    }

    if (i % 50 === 49 && i + 1 < n) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  return { conversations: parsed, parseErrors: errors };
}

// ─── Unified Entry Point ─────────────────────────────────────────────────────

/**
 * Auto-detects the source and parses conversations into the normalized format.
 * Returns chats, source folders, and any parsing/restoration warnings.
 */
export async function parseConversations(rawConversations, { collectionMetadata = [], sourceHint } = {}) {
  const rows = conversationExportRows(rawConversations);
  const emptyArchive = rows?.length === 0;
  const source = detectSource(rawConversations) || (emptyArchive && ['chatgpt', 'claude', 'grok', 'gabai'].includes(sourceHint) ? sourceHint : null);
  if (source) {
    const { collections, conversationCollectionIds, collectionWarnings } = parseImportCollections(source, rawConversations, collectionMetadata);
    if (source === 'chatgpt' && emptyArchive && collections.length === 0) {
      throw new Error('This ChatGPT export contains 0 conversations. Its conversation list is empty, so there are no chats to import. Check that your chats are visible in the ChatGPT account you exported, then request a new data export from that account. If the new export is also empty, contact OpenAI support.');
    }
    const parsers = { gabai: parseGabAIConversations, chatgpt: parseChatGPTConversations, claude: parseClaudeConversations, grok: parseGrokConversations };
    const input = source === 'grok' ? { conversations: rows } : rows;
    const result = await parsers[source](input, conversationCollectionIds);
    if (source === 'chatgpt' && rows.length > 0 && result.conversations.length === 0 && collections.length === 0) {
      throw new Error(`This ChatGPT file contains ${rows.length} conversation record(s), but no readable chat messages. The message tree may be missing, empty, or unsupported. Select the full export ZIP or all numbered conversations-*.json files. If those also fail, share this structure with support: ${describeConversationImportStructure(rawConversations)}`);
    }
    for (const conv of result.conversations) {
      const collectionId = conversationCollectionIds.get(conv.externalId);
      if (collectionId && !conv.collectionExternalId) conv.collectionExternalId = collectionId;
    }
    return { source, ...result, collections, collectionWarnings };
  }

  throw new Error(unrecognizedExportMessage(rawConversations));
}

function unrecognizedExportMessage(raw) {
  return `Unrecognized export format. Please use a data export from Gab AI, ChatGPT, Claude, or Grok. For ChatGPT, select conversations.json, the numbered conversations-*.json files, or the original export ZIP. Other account JSON files do not contain your chat history. File structure (no message text or account values): ${describeConversationImportStructure(raw)}`;
}

// ─── ZIP Extraction ──────────────────────────────────────────────────────────

function isConversationJsonFilename(name) {
  const base = (name.split('/').pop() || name).toLowerCase();
  return (
    base === 'conversations.json' ||
    /^conversations-\d+\.json$/.test(base) ||
    /^conversations\(\d+\)\.json$/.test(base)
  );
}

async function loadJsonFromZipEntry(zip, name) {
  const jsonString = await zip.file(name).async('string');
  return parseConversationJson(jsonString);
}

const UNZIP_CONVERSATIONS_HELP = 'Unzip the downloaded export and any inner conversation ZIPs, then select conversations.json or the numbered conversations-*.json files.';

/**
 * Extracts conversations JSON from a ChatGPT, Claude, Grok, or Gab AI export ZIP file.
 * Searches known JSON filenames, including ChatGPT's inner Conversations ZIPs,
 * without expanding the separate media archives. Falls back to probing JSON.
 */
export async function extractConversationsFromZip(zipData, { includeCollections = false } = {}) {
  const JSZip = await loadJSZip();
  const zip = await JSZip.loadAsync(zipData);
  const collectionMetadata = [];
  const conversations = await extractConversationsFromLoadedZip(zip, JSZip, 0, includeCollections ? collectionMetadata : null);
  return includeCollections ? { object: 'conversation_import_archive', conversations, collectionMetadata } : conversations;
}

async function extractConversationsFromLoadedZip(zip, JSZip, depth, collectionMetadata) {
  const allFiles = Object.keys(zip.files).filter((name) =>
    !zip.files[name].dir &&
    !name.split('/').some(part => part === '__MACOSX' || part.startsWith('._'))
  );

  const knownConversationFiles = allFiles
    .filter(isConversationJsonFilename)
    .sort();

  if (collectionMetadata) {
    for (const name of allFiles.filter(importMetadataKind).sort()) {
      const kind = importMetadataKind(name);
      try { collectionMetadata.push({ kind, rows: await loadJsonFromZipEntry(zip, name) }); }
      catch { collectionMetadata.push({ kind, rows: null }); }
    }
  }

  const innerConversationZips = allFiles.filter(name =>
    /^conversations.*\.zip$/i.test(name.split('/').pop())
  ).sort();

  if (knownConversationFiles.length > 1 || innerConversationZips.length > 0) {
    const merged = [];
    let source = null;
    const appendConversations = (data, name) => {
      const rows = conversationExportRows(data);
      if (collectionMetadata) collectionMetadata.push(...collectImportMetadata(data));
      if (rows?.length === 0) return;
      const chunkSource = detectSource(data);
      if (!rows || !chunkSource) {
        throw new Error(`Unrecognized conversation chunk "${name}". ${unrecognizedExportMessage(data)} ${UNZIP_CONVERSATIONS_HELP}`);
      }
      if (source && source !== chunkSource) {
        throw new Error('This ZIP contains conversations from different sources. Import each source separately.');
      }
      source = chunkSource;
      for (const conversation of rows) merged.push(conversation);
    };

    for (const name of knownConversationFiles) {
      let data;
      try {
        data = await loadJsonFromZipEntry(zip, name);
      } catch {
        throw new Error(`Could not read conversation chunk "${name}". Download the export again or upload its valid JSON files separately.`);
      }
      appendConversations(data, name);
    }
    for (const name of innerConversationZips) {
      // Bound recursive archive expansion; deeper exports can be unzipped locally.
      if (depth >= 3) {
        throw new Error(`Conversation archive "${name}" is nested too deeply. ${UNZIP_CONVERSATIONS_HELP}`);
      }
      let data;
      try {
        const bytes = await zip.file(name).async('uint8array');
        const innerZip = await JSZip.loadAsync(bytes);
        data = await extractConversationsFromLoadedZip(innerZip, JSZip, depth + 1, collectionMetadata);
      } catch (error) {
        throw new Error(`Could not read conversation archive "${name}": ${error.message} ${UNZIP_CONVERSATIONS_HELP}`);
      }
      appendConversations(data, name);
    }
    return merged;
  }

  const knownGrokFiles = allFiles.filter((name) =>
    name.endsWith('prod-grok-backend.json')
  );

  const priorityFiles = [...knownConversationFiles, ...knownGrokFiles];
  for (const name of priorityFiles) {
    let data;
    try {
      data = await loadJsonFromZipEntry(zip, name);
    } catch {
      throw new Error(`Could not read conversation file "${name}". Download the export again or upload its valid JSON files separately.`);
    }
    if (detectSource(data) || conversationExportRows(data)?.length === 0) return data;
    // A known conversation file was found. Preserve its parsing failure
    // instead of reporting that the ZIP contains no conversation data.
    throw new Error(`Could not understand "${name}": ${unrecognizedExportMessage(data)}`);
  }

  const prioritySet = new Set(priorityFiles);
  const remainingJsonFiles = allFiles.filter(
    (name) => name.toLowerCase().endsWith('.json') && !prioritySet.has(name)
  );

  for (const name of remainingJsonFiles) {
    try {
      const data = await loadJsonFromZipEntry(zip, name);
      if (detectSource(data)) return data;
    } catch {
      /* not the right file, try next */
    }
  }

  if (collectionMetadata?.length) return [];

  if (allFiles.some(name => name.toLowerCase().endsWith('.zip'))) {
    throw new Error(`No conversation JSON found outside the inner ZIP files. ${UNZIP_CONVERSATIONS_HELP}`);
  }

  throw new Error(
    'No conversation data found in the ZIP file. Make sure this is a data export from Gab AI, ChatGPT, Claude, or Grok.'
  );
}

// ─── Batching ────────────────────────────────────────────────────────────────

/** Must stay in sync with backend IMPORT_MAX_CONVERSATIONS per request. */
export const IMPORT_CONVERSATIONS_BATCH_SIZE = 500;
/**
 * Stay well below backend/proxy body limits so very large exports still import
 * cleanly even when one provider's conversations are much larger than another's.
 * Smaller chunks = more HTTP round-trips but faster first response and smoother % progress.
 */
export const IMPORT_TARGET_REQUEST_BYTES = 2 * 1024 * 1024;

const _cachedEncoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

function estimateUtf8Bytes(value) {
  if (_cachedEncoder) {
    return _cachedEncoder.encode(value).length;
  }
  // React Native may not provide TextEncoder. Count UTF-8, including surrogate pairs.
  let bytes = 0;
  for (const character of value) {
    const code = character.codePointAt(0);
    bytes += code <= 0x7f ? 1 : code <= 0x7ff ? 2 : code <= 0xffff ? 3 : 4;
  }
  return bytes;
}

function estimateJsonBytes(value) {
  return estimateUtf8Bytes(JSON.stringify(value));
}

function buildConversationPartTitle(title, index, total) {
  return total > 1 ? `${title} (Part ${index}/${total})` : title;
}

function findConversationSplitPoint(messages) {
  const midpoint = Math.floor(messages.length / 2);

  for (let i = midpoint; i < messages.length - 1; i++) {
    if (messages[i]?.role === 'assistant' && messages[i + 1]?.role === 'user') {
      return i + 1;
    }
  }

  for (let i = midpoint; i > 0; i--) {
    if (messages[i - 1]?.role === 'assistant' && messages[i]?.role === 'user') {
      return i;
    }
  }

  return midpoint;
}

function splitConversationMessages(conv, maxConversationBytes) {
  if (conv.messages.length <= 10000 && estimateJsonBytes(conv) <= maxConversationBytes) {
    return [conv.messages];
  }

  const messages = Array.isArray(conv.messages) ? conv.messages : [];
  if (messages.length <= 1) {
    return [messages];
  }

  const shellBytes = estimateJsonBytes({
    ...conv,
    messages: [],
  });

  const parts = [];
  let currentMessages = [];
  let currentBytes = shellBytes;

  for (const msg of messages) {
    const msgBytes = estimateJsonBytes(msg);
    const nextBytes = currentBytes + msgBytes + (currentMessages.length > 0 ? 1 : 0);

    if (currentMessages.length > 0 && (nextBytes > maxConversationBytes || currentMessages.length >= 10000)) {
      parts.push(currentMessages);
      currentMessages = [msg];
      currentBytes = shellBytes + msgBytes;
      continue;
    }

    currentMessages.push(msg);
    currentBytes = nextBytes;
  }

  if (currentMessages.length > 0) {
    parts.push(currentMessages);
  }

  if (parts.length > 1) {
    return parts.flatMap((partMessages) =>
      splitConversationMessages(
        {
          ...conv,
          messages: partMessages,
        },
        maxConversationBytes
      )
    );
  }

  const splitPoint = findConversationSplitPoint(messages);
  if (splitPoint <= 0 || splitPoint >= messages.length) {
    return [messages];
  }

  return [
    ...splitConversationMessages(
      {
        ...conv,
        messages: messages.slice(0, splitPoint),
      },
      maxConversationBytes
    ),
    ...splitConversationMessages(
      {
        ...conv,
        messages: messages.slice(splitPoint),
      },
      maxConversationBytes
    ),
  ];
}

function splitConversationForImport(conv, maxConversationBytes) {
  const messageParts = splitConversationMessages(conv, maxConversationBytes);

  if (messageParts.length <= 1) {
    return [conv];
  }

  return messageParts.map((messages, index) => {
    // Each part has to get its OWN stable externalId, otherwise the unique
    // index on (user, importSource, importExternalId) would reject parts
    // 2..N as duplicates of part 1 on the very first import. Derive per-part
    // ids deterministically so re-imports upsert the same rows.
    let partExternalId = null;
    if (conv.externalId) {
      partExternalId =
        index === 0
          ? conv.externalId
          : `${conv.externalId}::part-${index + 1}-of-${messageParts.length}`;
    }

    return {
      ...conv,
      title: buildConversationPartTitle(conv.title || 'Imported Conversation', index + 1, messageParts.length),
      created_at: messages[0]?.timestamp || conv.created_at,
      // Each split part should land in the sidebar at its own slice of time
      // — the last message in *that part*, not the original conversation's
      // overall update time. Otherwise every part of a big export would
      // collapse onto the same instant.
      updated_at:
        messages[messages.length - 1]?.timestamp ||
        conv.updated_at ||
        messages[0]?.timestamp ||
        conv.created_at,
      externalId: partExternalId,
      messages,
    };
  });
}

/**
 * Builds upload batches. Yields to the event loop while splitting large exports so the UI
 * stays responsive (pure sync batching can block for minutes on multi‑MB ChatGPT JSON).
 *
 * @param {(processed: number, total: number) => void} [onPrepareProgress] — called during split phase
 */
export async function batchConversationsAsync(
  source,
  conversations,
  onPrepareProgress
) {
  const batches = [];
  const requestBaseBytes = estimateJsonBytes({ source, conversations: [] });
  const maxConversationBytes = Math.max(1024, IMPORT_TARGET_REQUEST_BYTES - requestBaseBytes - 1024);
  const preparedConversations = [];
  const n = conversations.length;

  for (let i = 0; i < n; i++) {
    const parts = splitConversationForImport(conversations[i], maxConversationBytes);
    preparedConversations.push(...parts);
    if (i % 40 === 39 && i + 1 < n) {
      await new Promise((resolve) => setTimeout(resolve, 0));
      onPrepareProgress?.(i + 1, n);
    }
  }
  if (n > 0) {
    onPrepareProgress?.(n, n);
  }

  let currentBatch = [];
  let currentBatchBytes = requestBaseBytes;

  for (const conv of preparedConversations) {
    const conversationBytes = estimateJsonBytes(conv);
    const nextBatchBytes =
      currentBatchBytes + conversationBytes + (currentBatch.length > 0 ? 1 : 0);

    if (
      currentBatch.length > 0 &&
      (currentBatch.length >= IMPORT_CONVERSATIONS_BATCH_SIZE ||
        nextBatchBytes > IMPORT_TARGET_REQUEST_BYTES)
    ) {
      batches.push(currentBatch);
      currentBatch = [];
      currentBatchBytes = requestBaseBytes;
    }

    currentBatch.push(conv);
    currentBatchBytes += conversationBytes + (currentBatch.length > 1 ? 1 : 0);
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
}
